import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PageHeaderComponent } from '@shared/components/layout';
import { CellEditSaveEvent, FilterChip, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import {
  DataTableMultiSelectFilterComponent,
  DataTableMultiSelectFilterOption,
} from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { NotificationService } from '@shared/services/notification.service';
import { DeleteConfirmDialogComponent } from '../../../user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import {
  ActivityConfigurationDto,
  ActivityManagementService,
  UpsertActivityConfigurationDto,
} from '../../services/activity-management.service';
import { AreaDto, AreaManagementService } from '../../services/area-management.service';

@Component({
  selector: 'app-activity-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PageHeaderComponent,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
  ],
  templateUrl: './activity-list.component.html',
  styleUrl: './activity-list.component.scss',
})
export class ActivityListComponent implements OnInit, OnDestroy {
  areaOptions: AreaDto[] = [];

  readonly organizationLevelOptions = [
    { value: 1, label: 'Iglesia Regional' },
    { value: 2, label: 'Area Apostol' },
    { value: 3, label: 'Distrito' },
    { value: 4, label: 'Grupo Comunidad' },
    { value: 5, label: 'Comunidad' },
  ];

  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', hideable: true, visible: true, resizable: true, editable: true, editType: 'text' },
    {
      key: 'area',
      header: 'Area',
      hideable: true,
      visible: true,
      resizable: true,
      editable: true,
      editType: 'select',
      editOptions: [],
    },
    {
      key: 'organizationLevel',
      header: 'Nivel',
      hideable: true,
      visible: true,
      resizable: true,
    },
    { key: 'roles', header: 'Roles', hideable: true, visible: true, resizable: true },
    { key: 'isActive', header: 'Activo', hideable: true, visible: true, resizable: true },
    { key: 'actions', header: 'Acciones', width: '1%', minWidth: '104px', exportable: false },
  ];

  tableData: ActivityConfigurationDto[] = [];
  isLoading = false;
  savingCell?: { row: ActivityConfigurationDto; column: string };
  activeFilterChips: FilterChip[] = [];
  areaSelectOptions: DataTableMultiSelectFilterOption[] = [];
  levelSelectOptions: DataTableMultiSelectFilterOption[] = [];
  allActivities: ActivityConfigurationDto[] = [];
  readonly statusFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'active', label: 'Activas' },
    { value: 'inactive', label: 'Inactivas' },
  ];
  readonly filterForm = new FormGroup({
    searchTerm: new FormControl('', { nonNullable: true }),
    status: new FormControl<string[]>(['active'], { nonNullable: true }),
    areaId: new FormControl<string[]>([], { nonNullable: true }),
    level: new FormControl<string[]>([], { nonNullable: true }),
  });

  private readonly destroy$ = new Subject<void>();
  private areaFilterInitialized = false;
  private levelFilterInitialized = false;

  constructor(
    private readonly service: ActivityManagementService,
    private readonly areaService: AreaManagementService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadAreas();
    this.loadActivities();

    this.filterForm.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) =>
          previous.searchTerm === current.searchTerm &&
          (previous.status ?? []).join(',') === (current.status ?? []).join(',') &&
          (previous.areaId ?? []).join(',') === (current.areaId ?? []).join(',') &&
          (previous.level ?? []).join(',') === (current.level ?? []).join(',')
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyFilter());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createNew(): void {
    this.router.navigate(['/configuration/activities/new']);
  }

  editActivity(activity: ActivityConfigurationDto): void {
    this.router.navigate(['/configuration/activities', activity.id, 'edit']);
  }

  deleteActivity(activity: ActivityConfigurationDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar colaboracion',
        message: `Estas seguro de que deseas eliminar la colaboracion "${activity.name}"?`,
      },
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.confirmDelete(activity.id);
        }
      });
  }

  getAreaLabel(activity: ActivityConfigurationDto): string {
    return activity.areaName || '-';
  }

  getLevelsText(activity: ActivityConfigurationDto): string {
    return activity.allowedOrganizationLevelNames.join(', ') || '-';
  }

  getRolesText(activity: ActivityConfigurationDto): string {
    return activity.roles.map(role => role.name).join(', ') || '-';
  }

  onToolbarSearch(term: string): void {
    this.filterForm.controls.searchTerm.setValue(term);
  }

  onFilterRemove(key: string): void {
    switch (key) {
      case 'status':
        this.filterForm.controls.status.setValue(this.statusFilterOptions.map(option => option.value), { emitEvent: false });
        break;
      case 'areaId':
        this.filterForm.controls.areaId.setValue(this.areaSelectOptions.map(option => option.value), { emitEvent: false });
        break;
      case 'level':
        this.filterForm.controls.level.setValue(this.levelSelectOptions.map(option => option.value), { emitEvent: false });
        break;
      default:
        return;
    }

    this.applyFilter();
  }

  onStatusChange(activity: ActivityConfigurationDto, newStatus: boolean): void {
    const oldStatus = activity.isActive;
    activity.isActive = newStatus;
    this.cdr.markForCheck();

    this.service.update(activity.id, this.toUpsertDto(activity))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success(newStatus ? 'Colaboracion activada exitosamente' : 'Colaboracion desactivada exitosamente');
          this.applyFilter();
        },
        error: (error: any) => {
          console.error('Error updating activity status:', error);
          activity.isActive = oldStatus;
          this.notificationService.error(error?.error?.message || 'Error al actualizar el estado');
          this.loadActivities();
          this.cdr.markForCheck();
        },
      });
  }

  onCellEditSave(event: CellEditSaveEvent<ActivityConfigurationDto>): void {
    if (!['name', 'area'].includes(event.column)) {
      return;
    }

    const activity = event.row;
    const previous = this.cloneActivity(activity);
    const nextValue = String(event.newValue ?? '').trim();

    if (event.column === 'name' && !nextValue) {
      this.notificationService.warning('El nombre no puede quedar vacio');
      return;
    }

    if (event.column === 'name') {
      activity.name = nextValue;
    } else if (event.column === 'area') {
      activity.areaId = Number(event.newValue);
      const area = this.areaOptions.find(option => option.id === activity.areaId);
      activity.areaCode = area?.code ?? activity.areaCode;
      activity.areaName = area?.name ?? activity.areaName;
    }

    this.savingCell = { row: activity, column: event.column };
    this.cdr.markForCheck();

    this.service.update(activity.id, this.toUpsertDto(activity))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Colaboracion actualizada exitosamente');
          this.savingCell = undefined;
          this.refreshFilterOptions();
          this.applyFilter();
        },
        error: (error: any) => {
          console.error('Error updating activity:', error);
          Object.assign(activity, previous);
          this.notificationService.error(error?.error?.message || 'Error al actualizar la colaboracion');
          this.savingCell = undefined;
          this.loadActivities();
          this.cdr.markForCheck();
        },
      });
  }

  private loadActivities(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: activities => {
          this.allActivities = activities ?? [];
          this.isLoading = false;
          this.cdr.markForCheck();
          this.refreshFilterOptions();
          this.applyFilter();
        },
        error: () => {
          this.notificationService.error('Error al cargar las colaboraciones');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private confirmDelete(id: number): void {
    this.service.delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Colaboracion eliminada exitosamente');
          this.loadActivities();
        },
        error: (error: any) => {
          this.notificationService.error(error?.error?.message || 'Error al eliminar la colaboracion');
          this.cdr.markForCheck();
        },
      });
  }

  private applyFilter(): void {
    const filters = this.filterForm.getRawValue();
    const searchTerm = String(filters.searchTerm ?? '').trim().toLowerCase();

    let filteredData = this.allActivities;

    if (filters.status.length === 0 || filters.areaId.length === 0 || filters.level.length === 0) {
      filteredData = [];
    } else {
      filteredData = filteredData.filter(activity =>
        filters.status.includes(activity.isActive ? 'active' : 'inactive') &&
        filters.areaId.includes(String(activity.areaId)) &&
        activity.allowedOrganizationLevelIds.some(levelId => filters.level.includes(String(levelId)))
      );
    }

    if (searchTerm) {
      filteredData = filteredData.filter(activity =>
        activity.name.toLowerCase().includes(searchTerm) ||
        this.getAreaLabel(activity).toLowerCase().includes(searchTerm) ||
        this.getLevelsText(activity).toLowerCase().includes(searchTerm) ||
        this.getRolesText(activity).toLowerCase().includes(searchTerm)
      );
    }

    this.tableData = [...filteredData];
    this.buildFilterChips();
    this.cdr.markForCheck();
  }

  private buildFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];

    this.addMultiSelectChip(chips, 'status', 'Estado', filters.status, this.statusFilterOptions);
    this.addMultiSelectChip(chips, 'areaId', 'Área', filters.areaId, this.areaSelectOptions);
    this.addMultiSelectChip(chips, 'level', 'Nivel', filters.level, this.levelSelectOptions);

    this.activeFilterChips = chips;
  }

  private refreshFilterOptions(): void {
    const areas = new Map<string, string>();
    this.allActivities.forEach(activity => {
      areas.set(String(activity.areaId), activity.areaName || `Área ${activity.areaId}`);
    });
    this.areaSelectOptions = [...areas.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

    const usedLevelIds = new Set(this.allActivities.flatMap(activity => activity.allowedOrganizationLevelIds));
    this.levelSelectOptions = this.organizationLevelOptions
      .filter(level => usedLevelIds.has(level.value))
      .map(level => ({ value: String(level.value), label: level.label }));

    if (!this.areaFilterInitialized && this.areaSelectOptions.length > 0) {
      this.areaFilterInitialized = true;
      this.filterForm.controls.areaId.setValue(this.areaSelectOptions.map(option => option.value), { emitEvent: false });
    }
    if (!this.levelFilterInitialized && this.levelSelectOptions.length > 0) {
      this.levelFilterInitialized = true;
      this.filterForm.controls.level.setValue(this.levelSelectOptions.map(option => option.value), { emitEvent: false });
    }

    this.columns = this.columns.map(column =>
      column.key === 'area'
        ? { ...column, editOptions: this.areaOptions.map(area => ({ value: area.id, label: area.name })) }
        : column
    );
  }

  private addMultiSelectChip(
    chips: FilterChip[],
    key: string,
    label: string,
    values: readonly string[],
    options: readonly DataTableMultiSelectFilterOption[],
  ): void {
    if (values.length === options.length) {
      return;
    }

    const selection = values.length === 0
      ? 'Ninguno'
      : values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');
    chips.push({ key, label: `${label}: ${selection}`, removable: true });
  }

  private toUpsertDto(activity: ActivityConfigurationDto): UpsertActivityConfigurationDto {
    return {
      name: activity.name,
      description: activity.description ?? null,
      areaId: activity.areaId,
      allowedOrganizationLevelIds: activity.allowedOrganizationLevelIds,
      isActive: activity.isActive,
      roles: activity.roles.map(role => ({
        id: role.id,
        name: role.name,
        shortName: role.shortName ?? null,
        description: role.description ?? null,
        isActive: role.isActive,
      })),
    };
  }

  private cloneActivity(activity: ActivityConfigurationDto): ActivityConfigurationDto {
    return {
      ...activity,
      roles: activity.roles.map(role => ({ ...role })),
    };
  }

  private loadAreas(): void {
    this.areaService.getActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: areas => {
          this.areaOptions = areas;
          this.refreshFilterOptions();
          this.cdr.markForCheck();
        },
        error: () => this.notificationService.error('No se pudieron cargar las áreas'),
      });
  }
}
